import { useState, useCallback, useEffect } from 'react';
import { readValue, writeValue } from '../utils/safeStorageUtils';

interface IUseStorageHook<T> {
	storageValue?: T | string | undefined;
	setValue: (newValue: T) => void;
	removeItem: () => void;
}

interface UseStorageArgs<T> {
	key: string;
	storage?: Storage;
	value?: T;
}

declare global {
	interface WindowEventMap {
		'session-storage': CustomEvent;
	}
}

const STORAGE_CHANGE_CUSTOM_EVENT = 'storage-change';

export function useBrowserStorage<T = string>({
	key,
	value,
	storage = globalThis.localStorage,
}: UseStorageArgs<T>): IUseStorageHook<T> {
	const [storageValue, setStorageValue] = useState<T | undefined>(() =>
		readValue({ key, defaultValue: value, storage })
	);

	const setValue = useCallback(
		(newValue: T) => {
			try {
				writeValue({ key, value: newValue, storage });
				setStorageValue(newValue);
				// Dispatch a custom event to notify every useSessionStorage hook
				globalThis.dispatchEvent(new Event(STORAGE_CHANGE_CUSTOM_EVENT));
			} catch (err) {
				console.warn('Failed to write to storage - value', err);
			}
		},
		[key, storage]
	);

	const handleStorageChange = useCallback(
		() => setStorageValue(readValue({ key, defaultValue: value, storage }) as T),
		[key, storage, value]
	);

	useEffect(() => {
		globalThis.addEventListener(STORAGE_CHANGE_CUSTOM_EVENT, handleStorageChange);
		return () => {
			globalThis.removeEventListener(STORAGE_CHANGE_CUSTOM_EVENT, handleStorageChange);
		};
	}, [handleStorageChange]);

	const removeItem = useCallback(() => {
		try {
			storage?.removeItem(key);
			// Dispatch a custom event to notify every useSessionStorage hook
			globalThis.dispatchEvent(new Event(STORAGE_CHANGE_CUSTOM_EVENT));
		} catch (err) {
			console.error('Failed to remove to storage - value', err);
		}
	}, [key, storage]);

	return { storageValue, setValue, removeItem };
}
